/**
 * The donor's display currency, held once per DONATE root (CON-313, SPEC 3.C1).
 *
 * Mounted at every root that can post DONATE: EmbedForm (which hosted /donate
 * pages also render), EventForm, and App.jsx's AddToCartSheet root; App.jsx's
 * standalone StandardForm root gets it with `enabled={false}`.
 *
 * THE RULE: whenever anything is missing, stale, malformed or switched off, the
 * answer is USD with no rate attached, and the form looks and posts exactly as
 * it does today. Concretely:
 *   - conversion is on only when `enabled` and `settings.enabled === true`
 *     (absent, `false`, `"true"`, anything else: off);
 *   - the rates request starts only then, and only with a configured
 *     `VITE_FX_RATES_URL` (D-R41): an org that is off makes no extra request;
 *   - the first frame is USD unless the table is already in this document; it
 *     switches at most ONCE, and never after the donor interacted with the form
 *     (§2.4) — `latch` records the first pointerdown/keydown/change inside
 *     `scopeRef` (anywhere in the document without one).
 *
 * THE INVARIANT CON-316 relies on: a non-USD `displayCurrency` always comes with
 * `fxRate` (the table's string), `fxRateTableId`, `fxRateAsOf` and `fxRatesDate`
 * (the response's raw Date header) from ONE table; USD carries none of them.
 *
 * It renders only the context provider: no DOM node. Outside any provider the
 * hook returns a frozen USD default, so standalone widgets and the dashboard
 * previewer never throw.
 *
 * CON-750, the side panel: the hosted page and the overlay draw the form twice
 * (rail and drawer), each copy with its own provider, and the fundraiser bar and
 * donor list beside it sit outside both. A provider mounted with `publish`
 * announces its value to the page once it is ready; `FollowDisplayCurrency`
 * around the side panel shows the latest value announced, so a donor's switch in
 * either copy moves the bar and the list too. Nothing announced: the USD default.
 * The standalone bar and list mount their own provider with `track={false}`:
 * CURRENCY_SHOWN is a form event.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { trackForm, FORM_EVENTS } from '@charitystack/shared';
import { detectCurrency, OFFERED_CURRENCIES } from '@charitystack/shared/currency';
import { getFxRatesUrl } from '../config/env';
import { loadRates, readRatesSync } from './ratesClient';

const USD = 'USD';
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'change'];
const noop = () => {};

const DEFAULT_VALUE = Object.freeze({
  displayCurrency: USD,
  fxRate: null,
  fxRateTableId: null,
  fxRateAsOf: null,
  fxRatesDate: null,
  setDisplayCurrency: noop,
  applySettings: noop,
  ready: true,
  converted: false,
  conversionEnabled: false,
  donorCanChoose: false,
  availableCurrencies: Object.freeze([USD]),
});

const DisplayCurrencyContext = createContext(DEFAULT_VALUE);

/** Form keys that already sent CURRENCY_SHOWN in this document (the hosted page mounts EmbedForm twice). */
const shownKeys = new Set();

/** The fields a published value carries to the side panel (CON-750). */
const PUBLISHED_KEYS = Object.freeze([
  'displayCurrency',
  'fxRate',
  'fxRateTableId',
  'fxRateAsOf',
  'fxRatesDate',
  'converted',
  'conversionEnabled',
]);
/**
 * Each mounted `publish` provider's latest value, in the order they last changed; the newest is
 * what followers show. A provider announces only when ITS OWN value changes, so a hidden copy
 * re-rendering never takes the panel back from the copy the donor just used; on unmount it
 * withdraws, so a closed drawer never leaves its currency behind.
 */
const announcements = new Map();
let published = null;
const followers = new Set();
const readPublished = () => published;
function subscribePublished(listener) {
  followers.add(listener);
  return () => followers.delete(listener);
}
function setPublished(next) {
  if (next === published) return;
  published = next;
  followers.forEach((listener) => listener());
}
function announce(token, value) {
  const previous = announcements.get(token);
  if (previous && PUBLISHED_KEYS.every((key) => previous[key] === value[key])) return;
  const snapshot = Object.freeze(Object.fromEntries(PUBLISHED_KEYS.map((key) => [key, value[key]])));
  announcements.delete(token);
  announcements.set(token, snapshot);
  setPublished(snapshot);
}
function withdraw(token) {
  if (!announcements.delete(token)) return;
  setPublished([...announcements.values()].pop() ?? null);
}

/** Test seam. Never called by application code. */
export function __resetCurrencyShown() {
  shownKeys.clear();
  announcements.clear();
  setPublished(null);
}

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const isSettingsObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** settings.offered (or every offered code) ∩ the codes the table can price, USD always allowed. */
function pricedOffered(settings, table) {
  const offered = Array.isArray(settings?.offered) ? settings.offered : OFFERED_CURRENCIES;
  return offered.filter(
    (code) => code === USD || (OFFERED_CURRENCIES.includes(code) && hasOwn(table.rates, code)),
  );
}

/**
 * The automatic choice: USD with `default` when the table is missing or the
 * donor already touched the form; otherwise `detectCurrency` over the codes the
 * table can price (the restriction lives in its input only).
 */
function decide({ table, viewerCountry, settings, allowed, refused }) {
  if (!table || refused) return { currency: USD, detectedFrom: 'default', viewerCountry };
  let result;
  try {
    result = detectCurrency({
      viewerCountry,
      languages: typeof navigator === 'undefined' ? [] : navigator.languages,
      settings: { ...settings, offered: allowed },
    });
  } catch {
    result = null;
  }
  if (!result || (result.currency !== USD && !hasOwn(table.rates, result.currency))) {
    return { currency: USD, detectedFrom: 'default', viewerCountry };
  }
  return { currency: result.currency, detectedFrom: result.detectedFrom, viewerCountry };
}

export function useDisplayCurrency() {
  return useContext(DisplayCurrencyContext);
}

export function DisplayCurrencyProvider({
  config = null,
  enabled = true,
  scopeRef = null,
  latch = true,
  publish = false,
  track = true,
  children,
}) {
  const ownSettings = config?.currencySettings;
  const hasOwnSettings = ownSettings !== undefined && ownSettings !== null;
  const [applied, setApplied] = useState(null);
  const settings = applied ?? (isSettingsObject(ownSettings) ? ownSettings : null);
  const conversionOn = enabled === true && settings?.enabled === true;

  const url = getFxRatesUrl();
  const shouldFetch = conversionOn && url !== '';

  // The table as this document knows it: what the request settled to, or, for
  // a warm document, the cached value read synchronously on the first render.
  const [loaded, setLoaded] = useState(undefined);
  const rates = shouldFetch ? (loaded ?? readRatesSync(url)) : undefined;
  const table = rates?.table ?? null;

  // The automatic choice, made ONCE (USD when refused), and the donor's own.
  const [decision, setDecision] = useState(null);
  const [donorChoice, setDonorChoice] = useState(null);
  const interactedRef = useRef(false);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let live = true;
    loadRates({ url }).then((value) => {
      if (live) setLoaded(value);
    });
    return () => {
      live = false;
    };
  }, [shouldFetch, url]);

  // The interaction latch, armed from mount so a touch on the placeholder counts.
  useEffect(() => {
    if (!latch || typeof document === 'undefined') return undefined;
    const onInteraction = (event) => {
      const scope = scopeRef ? scopeRef.current : null;
      if (scopeRef && !(scope && event.target instanceof Node && scope.contains(event.target))) {
        return;
      }
      interactedRef.current = true;
      detach();
    };
    function detach() {
      INTERACTION_EVENTS.forEach((type) => document.removeEventListener(type, onInteraction, true));
    }
    INTERACTION_EVENTS.forEach((type) => document.addEventListener(type, onInteraction, true));
    return detach;
  }, [latch, scopeRef]);

  const allowed = useMemo(
    () => (conversionOn && table ? pricedOffered(settings, table) : null),
    [conversionOn, table, settings],
  );
  const availableCurrencies = useMemo(
    () =>
      allowed
        ? [USD, ...allowed.filter((code) => code !== USD)]
        : DEFAULT_VALUE.availableCurrencies,
    [allowed],
  );

  // The automatic choice is computed during the render in which the table is
  // first known (a warm document: the very first render), so no consumer ever
  // sees an intermediate USD value, and is then persisted once, before paint.
  const settledNow = conversionOn && (!shouldFetch || rates !== undefined);
  const pending =
    decision === null && settledNow
      ? decide({
          table,
          viewerCountry: rates?.viewerCountry ?? null,
          settings,
          allowed,
          refused: latch && interactedRef.current,
        })
      : null;
  const effectiveDecision = decision ?? pending;
  useLayoutEffect(() => {
    if (decision === null && pending !== null) setDecision(pending);
  }, [decision, pending]);

  const formKey = config?.formID ?? config?.elementID ?? 'cart';
  useEffect(() => {
    if (!track || table === null) return;
    if (!conversionOn || decision === null || shownKeys.has(formKey)) return;
    shownKeys.add(formKey);
    try {
      trackForm(FORM_EVENTS.CURRENCY_SHOWN, {
        display_currency: decision.currency,
        detected_from: decision.detectedFrom,
        viewer_country: decision.viewerCountry,
      });
    } catch (error) {
      console.error('[DisplayCurrencyProvider] CURRENCY_SHOWN failed:', error);
    }
  }, [track, conversionOn, table, decision, formKey]);

  // Accepted once, only at a root whose config has no currencySettings of its
  // own (the cart sheet fetches its config after mount, D-R5). Never throws.
  const acceptedRef = useRef(false);
  const hasOwnSettingsRef = useRef(hasOwnSettings);
  hasOwnSettingsRef.current = hasOwnSettings;
  const applySettings = useCallback((currencySettings) => {
    if (acceptedRef.current || hasOwnSettingsRef.current) return;
    if (!isSettingsObject(currencySettings)) return;
    acceptedRef.current = true;
    setApplied(currencySettings);
  }, []);

  // The donor's action (CON-314 emits CURRENCY_SWITCHED): only an available code.
  const availableRef = useRef(availableCurrencies);
  availableRef.current = availableCurrencies;
  const setDisplayCurrency = useCallback((code) => {
    if (!availableRef.current.includes(code)) return;
    interactedRef.current = true;
    setDonorChoice(code);
  }, []);

  const ready = !conversionOn || effectiveDecision !== null;
  let displayCurrency = conversionOn ? (donorChoice ?? effectiveDecision?.currency ?? USD) : USD;
  if (displayCurrency !== USD && !(table && availableCurrencies.includes(displayCurrency))) {
    displayCurrency = USD;
  }
  const converted = displayCurrency !== USD;

  const value = useMemo(
    () => ({
      displayCurrency,
      fxRate: converted ? table.rates[displayCurrency] : null,
      fxRateTableId: converted ? table.id : null,
      fxRateAsOf: converted ? table.asOf : null,
      fxRatesDate: converted ? rates.ratesDate : null,
      setDisplayCurrency,
      applySettings,
      ready,
      converted,
      /** On for this org AND a fresh table is loaded: a currency other than USD can be shown. */
      conversionEnabled: conversionOn && table !== null,
      donorCanChoose: conversionOn && settings?.donorCanChoose === true,
      availableCurrencies,
    }),
    [
      displayCurrency,
      converted,
      table,
      rates,
      setDisplayCurrency,
      applySettings,
      ready,
      conversionOn,
      settings,
      availableCurrencies,
    ],
  );

  // Before paint, so the side panel never shows a frame in another currency
  // than the form. Not before the config has landed or before `ready`: a copy
  // still loading would otherwise announce USD over the other copy's answer.
  const publishTokenRef = useRef(null);
  if (publishTokenRef.current === null) publishTokenRef.current = {};
  const hasConfig = config !== null && config !== undefined;
  useLayoutEffect(() => {
    if (publish && hasConfig && ready) announce(publishTokenRef.current, value);
  }, [publish, hasConfig, ready, value]);
  useLayoutEffect(() => {
    const token = publishTokenRef.current;
    return () => withdraw(token);
  }, []);

  return (
    <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>
  );
}

/**
 * The side panel's context (CON-750): the latest value a `publish` provider
 * announced, or the USD default. Display only — it cannot switch, accept
 * settings or show a selector.
 */
export function FollowDisplayCurrency({ children }) {
  const snapshot = useSyncExternalStore(subscribePublished, readPublished, readPublished);
  const value = useMemo(
    () => (snapshot ? Object.freeze({ ...DEFAULT_VALUE, ...snapshot }) : DEFAULT_VALUE),
    [snapshot],
  );
  return (
    <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>
  );
}

export default DisplayCurrencyProvider;
